import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { FormikErrors, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { InvoiceScriptEnum } from '../../../../common/enums/invoicing.enum';
import { getBillingSettings, updateBillingSettings } from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { bankAccountDigits, formatBankAccount, isValidBankAccount, normalizeBankAccountInput } from '../../../../utilities/helper/bankAccount.helper';
import { formatSerbianDate, toLocalDateString } from '../../../../utilities/helper/date.helper';
import { palette } from '../../../../theme';
import i18n from '../../../../i18n';
import { FieldFrame, FormTextField, messageId } from '../Form/FormField';
import SegmentedControl from '../../../../components/SegmentedControl';
import ErrorSummary, { SummaryItem } from '../Form/ErrorSummary';
import { readServerErrors } from '../Form/serverErrors';
import { collectErrors } from '../../validation/rules';
import {
  BILLING_SETTINGS_FIELDS,
  BLOCKING_WHEN_EMPTY,
  BillingSettingsField,
  BillingSettingsFormValues,
  billingSettingsSchema,
  billingSettingsWarnings,
  MISSING_FIELDS,
  PURPOSE_QR_MAX,
  PURPOSE_SAMPLE,
  PURPOSE_TOKENS,
  renderPurpose,
  toFormValues,
  toUpdateDto
} from '../../validation/billingSettings.schema';
import InvoiceHeaderPreview from './InvoiceHeaderPreview';
import { MISSING_LABELS } from './settingsLabels';
import { useUnsavedChanges } from './UnsavedChanges';

interface BillingSettingsTabProps {
  kindergartenId: string;
  /** After a save, so the setup cards above recount what is missing. */
  onSaved?: () => void;
}

export const fieldId = (field: BillingSettingsField) => `bs-${field}`;

/** What each field is called, in the summary and in the form. */
const LABELS: Record<BillingSettingsField, string> = {
  legalName: 'billing.settings.legalName',
  address: 'common.address',
  postalCode: 'billing.settings.postalCode',
  city: 'billing.settings.city',
  pib: 'billing.settings.pib',
  mb: 'billing.settings.mb',
  bankAccountNumber: 'billing.settings.bankAccount',
  bankName: 'billing.settings.bankName',
  paymentCode: 'billing.settings.paymentCode',
  defaultPaymentDueDays: 'billing.settings.dueDays',
  isInVatSystem: 'billing.settings.sections.vat',
  vatExemptionNote: 'billing.settings.vatExemption',
  vatRegistrationNumber: 'billing.settings.vatNumber',
  invoiceScript: 'billing.settings.script',
  contactEmail: 'billing.settings.contactEmail',
  contactPhone: 'billing.settings.contactPhone',
  invoiceGreetingMessage: 'billing.settings.greeting',
  invoiceFooterNote: 'billing.settings.footerNote',
  paymentPurposeTemplate: 'billing.settings.purpose'
};

const SECTIONS: { id: string; title: string; description: string; fields: BillingSettingsField[] }[] = [
  {
    id: 's-legal',
    title: 'billing.settings.sections.legalEntity',
    description: 'billing.settings.sectionHints.legalEntity',
    fields: ['legalName', 'address', 'postalCode', 'city', 'pib', 'mb']
  },
  {
    id: 's-payment',
    title: 'billing.settings.sections.payment',
    description: 'billing.settings.sectionHints.payment',
    fields: ['bankAccountNumber', 'bankName', 'paymentCode', 'defaultPaymentDueDays']
  },
  {
    id: 's-vat',
    title: 'billing.settings.sections.vat',
    description: 'billing.settings.sectionHints.vat',
    fields: ['isInVatSystem', 'vatExemptionNote', 'vatRegistrationNumber']
  },
  {
    id: 's-invoice',
    title: 'billing.settings.sections.invoiceAndContact',
    description: 'billing.settings.sectionHints.invoiceAndContact',
    fields: ['invoiceScript', 'contactEmail', 'contactPhone', 'invoiceGreetingMessage', 'invoiceFooterNote', 'paymentPurposeTemplate']
  }
];

const ALIASES = { PIB: 'pib', 'Company number': 'mb', 'Bank account': 'bankAccountNumber', 'Payment code': 'paymentCode' };

const TOKEN_CHIPS: Record<(typeof PURPOSE_TOKENS)[number], string> = {
  period: 'billing.settings.purposeChips.period',
  childName: 'billing.settings.purposeChips.childName',
  invoiceNumber: 'billing.settings.purposeChips.invoiceNumber'
};

/** "Issued 1. 11. 2026, due 16. 11. 2026": the due-day count, said as a date. */
const dueExample = (days: string): { issued: string; due: string } | null => {
  if (!/^\d+$/.test(days.trim()) || Number(days) > 365) return null;
  const now = new Date();
  const issued = new Date(now.getFullYear(), now.getMonth() + 1, 1, 12);
  const due = new Date(issued);
  due.setDate(due.getDate() + Number(days));
  return { issued: formatSerbianDate(toLocalDateString(issued)), due: formatSerbianDate(toLocalDateString(due)) };
};

const sameValue = (a: unknown, b: unknown) => (typeof a === 'string' && typeof b === 'string' ? a.trim() === b.trim() : a === b);

/**
 * What every invoice of the kindergarten says about who issued it and how to pay it.
 *
 * Always editable: there is no "Edit" mode, and an unsaved change shows a bar at the
 * bottom of the screen to save or discard it.
 *
 * Two kinds of problem, told apart on purpose. A wrong format - a 7-digit PIB, an account
 * whose control digits do not match - is an error, in red, and blocks saving, because the
 * server would refuse it or, worse, accept it and print a QR code no bank can read. An
 * empty field the invoice needs is only a warning, in orange: the server stores incomplete
 * settings and refuses at issue time, so a kindergarten can save what it has and finish
 * later. Neither is shown before the admin leaves the field or tries to save.
 */
const BillingSettingsTab: React.FC<BillingSettingsTabProps> = ({ kindergartenId, onSaved }) => {
  const { t } = useTranslation();
  const { setDirty } = useUnsavedChanges();
  const [loading, setLoading] = useState(true);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<Partial<Record<BillingSettingsField, string>>>({});
  const [serverOther, setServerOther] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const schema = useMemo(() => billingSettingsSchema(t), [t]);

  const formik = useFormik<BillingSettingsFormValues>({
    initialValues: toFormValues(null),
    validate: values => collectErrors(schema, values) as FormikErrors<BillingSettingsFormValues>,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: () => undefined
  });
  const { values, errors, touched, initialValues, resetForm, setFieldValue, setTouched, validateForm } = formik;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getBillingSettings(kindergartenId);
      resetForm({ values: toFormValues(result.results ?? null) });
    } catch {
      // the i18n instance rather than the hook's t: as a dependency, t would reload on a
      // language switch and overwrite a form being edited
      createNotification(MESSAGE_TYPE.ERROR, i18n.t('billing.settings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [kindergartenId, resetForm]);

  useEffect(() => {
    load();
  }, [load]);

  // a link such as the catalogue's "change it in Invoice details" points at a section
  const location = useLocation();
  useEffect(() => {
    if (loading || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView?.({ block: 'start' });
  }, [loading, location.hash]);

  const changed = BILLING_SETTINGS_FIELDS.filter(field => !sameValue(values[field], initialValues[field]));
  useEffect(() => {
    setDirty(changed.length > 0);
  }, [changed.length, setDirty]);
  useEffect(() => () => setDirty(false), [setDirty]);

  const warnings = billingSettingsWarnings(t, values);
  const shown = (field: BillingSettingsField) => attempted || !!touched[field];
  const errorOf = (field: BillingSettingsField) => (shown(field) ? serverErrors[field] || (errors[field] as string | undefined) : undefined);
  const warningOf = (field: BillingSettingsField) => (shown(field) && !errorOf(field) ? warnings[field] : undefined);

  const set = (field: BillingSettingsField, value: unknown) => {
    setFieldValue(field, value);
    if (serverErrors[field]) setServerErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const text = (field: BillingSettingsField) => ({
    id: fieldId(field),
    name: field,
    label: t(LABELS[field]),
    value: values[field] as string,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => set(field, event.target.value),
    onBlur: formik.handleBlur,
    error: errorOf(field),
    warning: warningOf(field),
    required: MISSING_FIELDS.includes(field)
  });

  const focusFirst = (failing: string[]) => {
    const first = BILLING_SETTINGS_FIELDS.find(field => failing.includes(field));
    if (first) document.getElementById(fieldId(first))?.focus();
  };

  const save = async () => {
    setAttempted(true);
    setTouched(Object.fromEntries(BILLING_SETTINGS_FIELDS.map(field => [field, true])), false);
    const found = await validateForm();
    const failing = [...Object.keys(found), ...Object.keys(serverErrors).filter(key => serverErrors[key as BillingSettingsField])];
    if (failing.length) {
      focusFirst(failing);
      return;
    }
    setSaving(true);
    setServerOther([]);
    try {
      const result = await updateBillingSettings(kindergartenId, toUpdateDto(values));
      resetForm({ values: toFormValues(result.results ?? null) });
      setAttempted(false);
      setServerErrors({});
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.settings.saved'));
      onSaved?.();
    } catch (error: any) {
      const read = readServerErrors(error, BILLING_SETTINGS_FIELDS, ALIASES);
      if (!read) {
        createNotification(MESSAGE_TYPE.ERROR, t('billing.settings.saveFailed'));
      } else {
        setServerErrors(read.fields as Partial<Record<BillingSettingsField, string>>);
        setServerOther(read.other);
        focusFirst(Object.keys(read.fields));
      }
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    resetForm({ values: initialValues });
    setAttempted(false);
    setServerErrors({});
    setServerOther([]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const fieldErrors = BILLING_SETTINGS_FIELDS.map(field => ({ field, message: errorOf(field) })).filter(entry => entry.message);
  const summary: SummaryItem[] = attempted
    ? [
        ...fieldErrors.map(entry => ({ id: fieldId(entry.field), label: t(LABELS[entry.field]), message: entry.message as string })),
        ...serverOther.map(message => ({ message }))
      ]
    : [];

  const sectionState = (fields: BillingSettingsField[]) => {
    // an empty name or payment code cannot be saved, so it counts before it is even shown
    const count = fields.filter(field => errorOf(field) || (BLOCKING_WHEN_EMPTY.includes(field) && !String(values[field]).trim())).length;
    if (count) return { tone: 'error.main', text: t('billing.settings.nav.errors', { count }) };
    const missing = fields.filter(field => warnings[field]);
    if (missing.length) {
      return { tone: 'warning.main', text: t('billing.settings.nav.missing', { what: missing.map(field => t(MISSING_LABELS[field])).join(', ') }) };
    }
    return { tone: 'success.main', text: t('billing.settings.nav.filled') };
  };

  const account = values.bankAccountNumber;
  const accountOk = !errorOf('bankAccountNumber') && isValidBankAccount(account);
  const due = dueExample(values.defaultPaymentDueDays);
  const purposeSample = renderPurpose(values.paymentPurposeTemplate.trim());
  const purposeTooLong = purposeSample.length > PURPOSE_QR_MAX;

  const insertToken = (token: string) => {
    const current = values.paymentPurposeTemplate;
    set('paymentPurposeTemplate', `${current}${current && !current.endsWith(' ') ? ' ' : ''}{${token}}`);
    document.getElementById(fieldId('paymentPurposeTemplate'))?.focus();
  };

  return (
    <Box sx={{ pb: changed.length ? 12 : 0 }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>{t('billing.settings.requiredLegend')}</Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', lg: '200px minmax(0, 1fr) 320px' },
          gridTemplateAreas: { xs: '"nav" "form" "preview"', lg: '"nav form preview"' }
        }}
      >
        <Box component="nav" aria-label={t('billing.settings.nav.ariaLabel')} sx={{ gridArea: 'nav', position: { lg: 'sticky' }, top: { lg: 24 } }}>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: { xs: 'row', lg: 'column' }, flexWrap: 'wrap', gap: { xs: 2, lg: 1.5 } }}>
            {SECTIONS.map(section => {
              const state = sectionState(section.fields);
              return (
                <li key={section.id}>
                  <Link href={`#${section.id}`} underline="hover" sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                    {t(section.title)}
                  </Link>
                  <Typography sx={{ fontSize: 12, color: state.tone }}>{state.text}</Typography>
                </li>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ gridArea: 'form', minWidth: 0 }}>
          <ErrorSummary items={summary} />

          <Stack spacing={3}>
            {SECTIONS.map(section => (
              <Box
                key={section.id}
                id={section.id}
                component="section"
                aria-labelledby={`${section.id}-title`}
                sx={{ border: 1, borderColor: 'line', borderRadius: 4, p: 3, scrollMarginTop: 24 }}
              >
                <Typography id={`${section.id}-title`} component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
                  {t(section.title)}
                </Typography>
                <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5, mb: 2.5 }}>{t(section.description)}</Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2, rowGap: 2.5 }}>
                  {section.id === 's-legal' && (
                    <>
                      <FormTextField {...text('legalName')} wide hint={t('billing.settings.hints.legalName')} />
                      <FormTextField {...text('address')} wide hint={t('billing.settings.hints.address')} />
                      <FormTextField {...text('postalCode')} hint={t('billing.settings.hints.postalCode')} />
                      <FormTextField {...text('city')} />
                      <FormTextField {...text('pib')} hint={t('billing.settings.hints.pib')} slotProps={{ htmlInput: { inputMode: 'numeric' } }} />
                      <FormTextField {...text('mb')} hint={t('billing.settings.hints.mb')} slotProps={{ htmlInput: { inputMode: 'numeric' } }} />
                    </>
                  )}

                  {section.id === 's-payment' && (
                    <>
                      <FormTextField
                        {...text('bankAccountNumber')}
                        wide
                        hint={t('billing.settings.hints.bankAccount')}
                        success={accountOk ? t('billing.settings.accountValid', { account: formatBankAccount(bankAccountDigits(account)) }) : undefined}
                        onBlur={event => {
                          const normalized = normalizeBankAccountInput(account);
                          if (normalized !== account) set('bankAccountNumber', normalized);
                          formik.handleBlur(event);
                        }}
                        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                      />
                      <FormTextField {...text('bankName')} />
                      <FormTextField {...text('paymentCode')} hint={t('billing.settings.hints.paymentCode')} slotProps={{ htmlInput: { inputMode: 'numeric' } }} />
                      <FormTextField
                        {...text('defaultPaymentDueDays')}
                        suffix={t('billing.settings.days')}
                        hint={due ? t('billing.settings.hints.dueDays', due) : undefined}
                        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                      />
                    </>
                  )}

                  {section.id === 's-vat' && (
                    <>
                      <FieldFrame id={fieldId('isInVatSystem')} labelId={`${fieldId('isInVatSystem')}-label`} label={t('billing.settings.vatStatus')} wide>
                        <SegmentedControl
                          id={fieldId('isInVatSystem')}
                          labelledBy={`${fieldId('isInVatSystem')}-label`}
                          value={values.isInVatSystem}
                          onChange={value => set('isInVatSystem', value)}
                          options={[
                            { value: false, label: t('billing.settings.notInVat') },
                            { value: true, label: t('billing.settings.inVat') }
                          ]}
                          width={420}
                        />
                      </FieldFrame>
                      {values.isInVatSystem ? (
                        <FormTextField {...text('vatRegistrationNumber')} wide />
                      ) : (
                        <FormTextField {...text('vatExemptionNote')} wide hint={t('billing.settings.vatExemptionHelp')} />
                      )}
                    </>
                  )}

                  {section.id === 's-invoice' && (
                    <>
                      <FieldFrame id={fieldId('invoiceScript')} labelId={`${fieldId('invoiceScript')}-label`} label={t(LABELS.invoiceScript)} wide>
                        <SegmentedControl
                          id={fieldId('invoiceScript')}
                          labelledBy={`${fieldId('invoiceScript')}-label`}
                          value={values.invoiceScript}
                          onChange={value => set('invoiceScript', value)}
                          options={[
                            { value: InvoiceScriptEnum.LATIN, label: t('billing.settings.scriptLatin') },
                            { value: InvoiceScriptEnum.CYRILLIC, label: t('billing.settings.scriptCyrillic') }
                          ]}
                          width={320}
                        />
                      </FieldFrame>
                      <FormTextField {...text('contactEmail')} type="email" hint={t('billing.settings.hints.contactEmail')} />
                      <FormTextField {...text('contactPhone')} />
                      <FormTextField {...text('invoiceGreetingMessage')} wide multiline minRows={2} maxLength={2000} />
                      <FormTextField
                        {...text('invoiceFooterNote')}
                        wide
                        multiline
                        minRows={2}
                        maxLength={2000}
                        hint={t('billing.settings.hints.footerNote')}
                        placeholder={t('billing.settings.footerPlaceholder')}
                      />
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <FormTextField
                          {...text('paymentPurposeTemplate')}
                          required
                          hint={
                            purposeTooLong
                              ? t('billing.settings.hints.purposeTooLong', {
                                  sample: purposeSample,
                                  n: purposeSample.length,
                                  max: PURPOSE_QR_MAX,
                                  cut: purposeSample.slice(0, PURPOSE_QR_MAX)
                                })
                              : t('billing.settings.hints.purpose', { sample: purposeSample, n: purposeSample.length, max: PURPOSE_QR_MAX })
                          }
                          hintTone={purposeTooLong ? 'warning' : undefined}
                        />
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('billing.settings.purposeChips.insert')}</Typography>
                          {PURPOSE_TOKENS.map(token => (
                            <ButtonBase
                              key={token}
                              onClick={() => insertToken(token)}
                              aria-describedby={messageId(fieldId('paymentPurposeTemplate'))}
                              sx={{ height: 28, px: 1.25, borderRadius: 999, bgcolor: 'surface.tint', color: 'primary.main', fontSize: 13 }}
                            >
                              {t(TOKEN_CHIPS[token], { sample: PURPOSE_SAMPLE[token] })}
                            </ButtonBase>
                          ))}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={{ gridArea: 'preview', position: { lg: 'sticky' }, top: { lg: 24 } }}>
          <InvoiceHeaderPreview values={values} />
        </Box>
      </Box>

      {changed.length > 0 && (
        <Box
          role="region"
          aria-label={t('billing.settings.unsaved.ariaLabel')}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            bgcolor: 'primary.main',
            color: palette.common.white,
            borderRadius: 4,
            boxShadow: 6,
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Typography sx={{ fontSize: 14, color: palette.common.white, mr: 1, whiteSpace: 'nowrap' }}>
            {t('billing.settings.unsaved.count', { count: changed.length })}
          </Typography>
          <ButtonBase onClick={discard} disabled={saving} sx={{ fontSize: 14, color: palette.common.white, px: 1.5, height: 36, borderRadius: 2, '&:hover': { textDecoration: 'underline' } }}>
            {t('billing.settings.unsaved.discard')}
          </ButtonBase>
          <ButtonBase
            onClick={save}
            disabled={saving}
            sx={{ fontSize: 14, fontWeight: 600, color: 'primary.main', bgcolor: palette.common.white, px: 2, height: 36, borderRadius: 2 }}
          >
            {saving ? <CircularProgress size={18} /> : t('billing.settings.unsaved.save')}
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

export default BillingSettingsTab;
